import { eosTool } from '@/services/blockchain/eos-tool'
import { usePairsStore } from '@/stores/usePairsStore'
import { decadd, decdiv, decmul, decsub, toFixed } from '@/lib/decimal'
import type { Pair, TokenInfo } from '@/types'

// Constants
const USDT_CONTRACT = 'tethertether'
const EOS_CONTRACT = 'eosio.token'
const EOS_PAIR_ID = 1
const TTR_PAIR_ID = 2

export interface FarmPool {
  id: number
  token: { sym: string; contract: string }
  weight: number
  total_weight: number
  total_balance: number
  total_token: number
  total_balance_usdt: number
  acc_reward_per_lp: number
  updated_time: number
  end_time: number
  status: number // 0=live, 1=finished
  apr: number
  token0?: TokenInfo
  token1?: TokenInfo
  // User fields
  isStake: number
  userToken: number
  userStaked: number
  userStakedUsdt: number
  debt: number
  reward: number
  claimed: number
  locked: number
  released: number
  unclaimed: number
  // Lend fields
  symbol?: string
  contract?: string
  avg_price?: number
  decimals?: number
  depositBalance?: number
  borrowCompound?: number
  [key: string]: any
}

export interface FarmGlobal {
  total_weight: number
  [key: string]: any
}

export interface FarmLock {
  id: number
  total: number
  released: number
  claimed: number
  start_time: number
  end_time: number
  [key: string]: any
}

/** Convert LP symbol name to pair ID */
function symbolToId(symbol: string): number {
  const s = symbol.length > 2 && symbol.substring(0, 2) === 'LP' ? symbol.substring(2) : symbol
  let ans = 0
  const len = s.length
  for (let i = 0, j = 1; i < len; i++, j *= 26) {
    ans += (s.charCodeAt(len - i - 1) - 64) * j
  }
  return ans
}

/** Fetch farm pools from contract */
export async function getFarms(contract = 'pools.ttr', lendList: any[] = []): Promise<FarmPool[]> {
  const rows = await eosTool.getTableRows<any>({
    code: contract, scope: contract, table: 'pools', json: true, limit: 1000,
  })
  if (rows.length > 0) formatFarms(rows, lendList)
  return rows || []
}

/** Fetch user staking info */
export async function getFarmInfoByUser(accountName: string, contract = 'pools.ttr') {
  const rows = await eosTool.getTableRows<any>({
    code: contract, scope: accountName, table: 'infos', json: true, limit: 1000,
  })
  return rows.length > 0 ? rows : []
}

/** Fetch user rewards */
export async function getFarmRewardByUser(accountName: string, contract = 'pools.ttr') {
  const rows = await eosTool.getTableRows<any>({
    code: contract, scope: accountName, table: 'rewards', json: true, limit: 1000,
  })
  return rows.length > 0 ? rows : []
}

/** Fetch global farm data */
export async function getFarmGlobal(contract = 'pools.ttr'): Promise<FarmGlobal> {
  const rows = await eosTool.getTableRows<any>({
    code: contract, scope: contract, table: 'global', json: true, limit: 10,
  })
  return rows.length > 0 ? rows[0] : { total_weight: 0 }
}

/** Fetch lock/vesting data */
export async function getFarmLocks(accountName: string, contract = 'pools.ttr'): Promise<FarmLock[]> {
  const rows = await eosTool.getTableRows<any>({
    code: contract, scope: accountName, table: 'locks', json: true, limit: 1000,
  })
  return rows.length > 0 ? rows : []
}

/** Internal: format farm pools with pair data and status */
function formatFarms(list: any[], lendList: any[] = []) {
  const pairs = usePairsStore.getState().pairs
  const nowMs = Date.now()

  for (const item of list) {
    const LP = item.token.sym.split(',')[1]
    const pairId = symbolToId(LP)
    const pair = pairs.find((p) => p.id === pairId)
    if (pair) {
      item.token0 = pair.token0
      item.token1 = pair.token1
    }

    // Check if finished
    item.status = nowMs > item.end_time * 1000 ? 1 : 0

    if (lendList.length === 0) {
      item.total_balance_usdt = getLpValue(item.token, item.total_balance, pair)
    } else {
      const lendItem = lendList.find(
        (s: any) =>
          s?.dsym?.split(',')[1] === item.token.sym.split(',')[1] ||
          s?.lsym?.split(',')[1] === item.token.sym.split(',')[1],
      )
      if (lendItem?.dsym?.split(',')[1] === item.token.sym.split(',')[1]) {
        item.total_balance_usdt = lendItem?.practical_balance && lendItem?.avg_price
          ? parseFloat(decmul(parseFloat(lendItem.practical_balance), lendItem.avg_price))
          : 0
      } else {
        item.total_balance_usdt = lendItem?.total_borrows_variable && lendItem?.avg_price
          ? parseFloat(decmul(parseFloat(lendItem.total_borrows_variable), lendItem.avg_price))
          : 0
      }
      item.symbol = lendItem?.symbol
      item.contract = lendItem?.contract
      item.avg_price = lendItem?.avg_price
      item.decimals = lendItem?.decimal
      item.depositBalance = lendItem?.depositBalance
      item.borrowCompound = lendItem?.borrowCompound
    }
  }
}

/** Format user staking info onto farm list */
export function formatFarmUserInfo(infoList: any[], farmList: FarmPool[], type = 'farm'): FarmPool[] {
  for (const farm of farmList) {
    farm.isStake = 0
    farm.userToken = 0
    farm.debt = 0
    farm.userStaked = 0
    farm.reward = 0
    farm.userStakedUsdt = 0

    for (const info of infoList) {
      if (farm.id === info.pool_id && info.token > 0) {
        farm.isStake = 1
        farm.userToken = info.token
        farm.userStaked = parseFloat(toFixed(
          Number(decmul(info.token, decdiv(farm.total_balance, farm.total_token))), 0,
        ))
        if (type === 'farm') {
          farm.userStakedUsdt = getLpValue(farm.token, farm.userStaked)
        } else {
          if (farm.token.contract === 'dtoken.ttr') {
            farm.userStakedUsdt = parseFloat(decmul(farm.depositBalance ?? 0, farm.avg_price ?? 0))
          } else {
            farm.userStakedUsdt = parseFloat(decmul(farm.borrowCompound ?? 0, farm.avg_price ?? 0))
          }
        }
        farm.debt = info.debt
        farm.reward = parseFloat(getReward(info, farm, type))
      }
    }
  }
  return farmList
}

/** Format user reward data onto farm list */
export function formatFarmReward(rewardList: any[], farmList: FarmPool[]): FarmPool[] {
  for (const farm of farmList) {
    farm.claimed = 0
    farm.locked = 0
    farm.released = 0
    farm.unclaimed = 0

    for (const r of rewardList) {
      if (farm.id === r.pool_id) {
        farm.claimed = r.claimed
        farm.locked = r.locked
        farm.released = r.released
        farm.unclaimed = r.unclaimed
      }
    }
  }
  return farmList
}

/** Calculate APR for liquidity mining farms (0.002 TTR per weight per second) */
export function formatFarmApr(global: FarmGlobal, farmList: FarmPool[]): FarmPool[] {
  for (const farm of farmList) {
    farm.total_weight = global.total_weight
    if (!farm.weight || !global.total_weight) {
      farm.apr = 0
    } else {
      const lpValue = getLpValue(farm.token, farm.total_balance)
      if (lpValue === 0) { farm.apr = 0; continue }
      farm.apr = (((farm.weight / global.total_weight) * 0.002 * getCoinPrice(TTR_PAIR_ID)) / lpValue) * 86400 * 365
    }
  }
  return farmList
}

/** Calculate APR for lend mining farms (0.001 TTR per weight per second) */
export function formatLendApr(global: FarmGlobal, farmList: FarmPool[]): FarmPool[] {
  for (const farm of farmList) {
    farm.total_weight = global.total_weight
    if (!farm.weight || !global.total_weight) {
      farm.apr = 0
    } else {
      if (farm.total_balance_usdt === 0) { farm.apr = 0; continue }
      farm.apr = (((farm.weight / global.total_weight) * 0.001 * getCoinPrice(TTR_PAIR_ID)) / farm.total_balance_usdt) * 86400 * 365
    }
  }
  return farmList
}

/** Calculate pending reward for a user's stake in a farm */
export function getReward(info: any, farm: any, type = 'farm'): string {
  const currentTimestamp = Date.now() / 1000
  const ttrReward = (currentTimestamp - farm.updated_time) * (type === 'farm' ? 2000 : 1000) * (farm.weight / farm.total_weight)
  const newAcc = decadd(farm.acc_reward_per_lp, decdiv(decmul(ttrReward, 100000000), farm.total_token))
  return decsub(decdiv(decmul(newAcc, info.token), 10 ** 8), info.debt)
}

/** Calculate LP token value in USDT */
export function getLpValue(coin: { sym: string; contract?: string }, quantity: number, pair?: Pair): number {
  let _pair: Pair | undefined = pair
  if (!_pair) {
    const pairs = usePairsStore.getState().pairs
    const LP = coin.sym.split(',')[1]
    const pairId = symbolToId(LP)
    _pair = pairs.find((p) => p.id === pairId)
  }
  if (!_pair) return 0
  const totalLiq = parseFloat(String(_pair.totalLiquidity))
  if (totalLiq === 0) return 0

  const reserve0 = parseFloat(_pair.token0.reserve.split(' ')[0].replace('.', ''))
  const reserve1 = parseFloat(_pair.token1.reserve.split(' ')[0].replace('.', ''))
  const perValue = quantity / totalLiq

  const isOnlyEos = _pair.token0.contract !== USDT_CONTRACT && _pair.token1.contract !== USDT_CONTRACT
  const reserve = isOnlyEos
    ? (_pair.token0.contract === EOS_CONTRACT ? reserve0 : reserve1)
    : (_pair.token0.contract === USDT_CONTRACT ? reserve0 : reserve1)

  const totalValue = (perValue * reserve) / 10 ** 4
  return isOnlyEos ? totalValue * 2 * getCoinPrice(EOS_PAIR_ID) : totalValue * 2
}

/** Get coin price from pair reserves (in USDT) */
export function getCoinPrice(pairId: number = EOS_PAIR_ID): number {
  const pairs = usePairsStore.getState().pairs
  const pair = pairs.find((p) => p.id === pairId)
  if (!pair) return 0

  const reserve0 = parseFloat(pair.token0.reserve.split(' ')[0].replace('.', ''))
  const reserve1 = parseFloat(pair.token1.reserve.split(' ')[0].replace('.', ''))
  if (pair.token0.contract === USDT_CONTRACT) {
    return reserve0 / reserve1
  }
  return reserve1 / reserve0
}
